import React from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { SFIcons } from "@sf-core";
import { CAROUSEL_TYPES } from "../../constants/carouselTypes";
import { profileActions } from "../../redux/reducers/profileReducer";
import { skillPathPageActions } from "../../redux/reducers/skillPathPageReducer";
import {
  getCourseUrl,
  getJobUrl,
  getProfileUrl,
  getSkillUrl,
  getSkillPathUrl,
} from "../../constants/urls";
import getStatusBadge from "../../constants/statusBadgeMappings";
import { saveCourseTrack } from "../../services/courseService";
import { STATUS } from "../../constants/statuses";
import { SFHelpers } from "@sf-core";
import CarouselComponent from "./carouselComponent";
import { PROFILE_STEPS } from "../../constants/profileSteps";

const SFCarousels = ({
  title,
  type,
  items = [],
  userJobRole,
  loading = false,
}) => {
  const location = useLocation();
  const dispatch = useDispatch();

  const lxpEnabledLearnerJobAndSkillVerification = useSelector(
    (state) =>
      state.session.tenantSettings.lxpEnabledLearnerJobAndSkillVerification
  );

  const onCardClick = (item, navigate) => {
    switch (type) {
    case CAROUSEL_TYPES.ContinueLearning:
      if (item.isSkillPath) {
        navigate(getSkillPathUrl(item.id), {
          state: { previousLoc: location.pathname },
        });
      } else {
        navigate(getCourseUrl(item.contentId), {
          state: { previousLoc: location.pathname },
        });
      }
      break;
    case CAROUSEL_TYPES.Courses:
      if (item.isSkillPath) {
        dispatch(
          skillPathPageActions.setState({
            skillPathId: item.id,
            domainId: item.domainId,
            domainName: item.jobRole,
            domainTypeId: 1,
            domainStatus: item.domainStatus,
          })
        );
        navigate(getSkillPathUrl(item.id), {
          state: { previousLoc: location.pathname },
        });
      } else {
        const data = {
          skillPathId: item.skillPathInfo
            ? Object.keys(item.skillPathInfo)[0]
            : null,
          contentId: item.id,
        };
        saveCourseTrack(data);
        navigate(getCourseUrl(item.id), {
          state: { previousLoc: location.pathname },
        });
      }
      break;
    case CAROUSEL_TYPES.Jobs:
    case CAROUSEL_TYPES.JobOfInterest:
      return navigate(getJobUrl(item.id));
    case CAROUSEL_TYPES.SkillGoals:
      return navigate(getSkillUrl(item.id));
    default:
      return () => {};
    }
  };

  const getDropDownActions = (navigate, t) => {
    switch (type) {
    case CAROUSEL_TYPES.Courses:
      return {
        actions: [
          {
            label: t("homeScreen.userRole.actionView"),
            onClick: () => {
              navigate(getJobUrl(userJobRole?.id));
            },
          },
          {
            ...(lxpEnabledLearnerJobAndSkillVerification && {
              label: t("homeScreen.userRole.actionManage"),
              onClick: () => {
                dispatch(
                  profileActions.setState({
                    activeStep: PROFILE_STEPS.JOB_ROLE,
                  })
                );
                navigate(getProfileUrl());
              },
            }),
          },
        ],
      };
    case CAROUSEL_TYPES.JobOfInterest:
      if (!lxpEnabledLearnerJobAndSkillVerification) return null;
      return {
        actions: [
          {
            label: t("homeScreen.jobOfInterest.action"),
            onClick: () => {
              dispatch(
                profileActions.setState({
                  activeStep: PROFILE_STEPS.JOB_INTEREST,
                })
              );
              navigate(getProfileUrl());
            },
          },
        ],
      };
    case CAROUSEL_TYPES.SkillGoals:
      if (!lxpEnabledLearnerJobAndSkillVerification) return null;
      return {
        actions: [
          {
            label: t("homeScreen.skillsGoals.action"),
            onClick: () => {
              dispatch(
                profileActions.setState({
                  activeStep: PROFILE_STEPS.SKILL_GOAL,
                })
              );
              navigate(getProfileUrl());
            },
          },
        ],
      };
    default:
      return null;
    }
  };

  const getCardStats = (item, t) => {
    switch (type) {
    case CAROUSEL_TYPES.ContinueLearning:
      if (item.isSkillPath) {
        return {
          badge: getStatusBadge(STATUS.IN_PROGRESS, t),
          indicator: t("homeScreen.skillsPath.indicator"),
          stats: [
            {
              label: t("homeScreen.skillsPath.label", {
                courses: item.totalCourses,
                level: item.level,
              }),
            },
          ],
          hoverStats: [
            {
              label: t("homeScreen.skillsPath.hoveredCourseLabel", {
                courses: item.totalCourses,
              }),
            },
            { label: item.level },
          ],
        };
      } else {
        return {
          badge: getStatusBadge(STATUS.IN_PROGRESS, t),
          stats: [
            {
              label: SFHelpers.DurationFormatter(
                item.duration,
                SFHelpers.DurationFormatterOptions.ALL_UNITS_BOLD
              ),
            },
          ],
          hoverStats: [
            {
              label: SFHelpers.DurationFormatter(
                item.duration,
                SFHelpers.DurationFormatterOptions.ALL_UNITS_BOLD
              ),
            },
          ],
        };
      }
    case CAROUSEL_TYPES.JobOfInterest:
    case CAROUSEL_TYPES.Jobs:
    case CAROUSEL_TYPES.SkillGoals:
      return {
        badge: getStatusBadge(item.status, t),
        stats:
            item.skillPaths < 1
              ? [
                {
                  label: t("homeScreen.relatedJobs.coursesSuffix", {
                    courses: item.totalCourses,
                  }),
                },
              ]
              : [
                {
                  label: t("homeScreen.relatedJobs.contentSuffix", {
                    courses: item.totalCourses,
                    recommendedSkillPaths: item.skillPaths,
                  }),
                },
              ],
        hoverStats:
            item.skillPaths < 1
              ? [
                {
                  label: t("homeScreen.relatedJobs.coursesSuffix", {
                    courses: item.totalCourses,
                  }),
                  icon: <SFIcons.SFBookIcon color="grey200" />,
                },
              ]
              : [
                {
                  label: t(
                    "homeScreen.relatedJobs.hoveredContentCoursesSuffix",
                    {
                      courses: item.totalCourses,
                    }
                  ),
                  icon: <SFIcons.SFBookIcon color="grey200" />,
                },
                {
                  label: t(
                    "homeScreen.relatedJobs.hoveredContentSkillPathsSuffix",
                    {
                      recommendedSkillPaths: item.skillPaths,
                    }
                  ),
                },
              ],
      };
    case CAROUSEL_TYPES.Courses:
      if (item.isSkillPath) {
        return {
          badge: getStatusBadge(item.status, t),
          indicator: t("homeScreen.skillsPath.indicator"),
          stats: [
            {
              label: t("homeScreen.skillsPath.label", {
                courses: item.totalCourses,
                level: item.level,
              }),
            },
          ],
          hoverStats: [
            {
              label: t("homeScreen.skillsPath.hoveredCourseLabel", {
                courses: item.totalCourses,
              }),
            },
            { label: item.level },
          ],
        };
      } else {
        return {
          badge: getStatusBadge(item.status, t),
          stats: [
            {
              label: SFHelpers.DurationFormatter(
                item.duration,
                SFHelpers.DurationFormatterOptions.ALL_UNITS_BOLD
              ),
            },
          ],
          hoverStats: [
            {
              label: SFHelpers.DurationFormatter(
                item.duration,
                SFHelpers.DurationFormatterOptions.ALL_UNITS_BOLD
              ),
            },
          ],
        };
      }
    default:
      return null;
    }
  };

  return (
    <CarouselComponent
      title={title}
      items={items}
      onCardClick={onCardClick}
      getCardStats={getCardStats}
      getDropDownActions={getDropDownActions}
      userJobRoleTitle={userJobRole?.title}
      showAvatar={type === CAROUSEL_TYPES.Courses}
      loading={loading}
    />
  );
};

export default SFCarousels;
